import { PaymentElement } from '@stripe/react-stripe-js';
import ReactModal from 'react-modal';
import { useEffect, useState } from "react";

import { Layout } from "@layouts";
import { Card } from "@molecules";
import { Button, Icon, Spinner } from "@atoms";
import { OnboardingAddCardTemplateProps } from "./";
import { styles as globalStyles } from "../../../styles/styles";
import { subscriptionPlanData } from "../billing/subscription-data";
import { SubscriptionPlansTypes } from "../../../types";
import { Bar } from '@organisms';
import { Button as SButton } from "@shadcn";
import { useTranslation } from "react-i18next";

export const OnboardingAddCardTemplate = ({
  modalIsOpen,
  closeModal,
  onPressAddCard,
  selectedCardIndex,
  cardDataList,
  handleSubmit,
  succeeded,
  processing,
  disabled,
  error,
  isAddressSubmitted,
  handleUpgradeClick,
  handleSendEmailClick,
  planAmount,
  onReturnPressed,
  onContinuePressed
}: OnboardingAddCardTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const [isPaymentElementReady, setPaymentElementReady] = useState(false);
  useEffect(() => {
    if (modalIsOpen) {
      setPaymentElementReady(false);
    }
  }, [modalIsOpen]);

  // const stripeCardStyle = {
  //   style: {
  //     base: {
  //       color: "#32325d",
  //       fontFamily: 'Arial, sans-serif',
  //       fontSmoothing: "antialiased",
  //       fontSize: "16px",
  //       "::placeholder": {
  //         color: "#32325d"
  //       }
  //     },
  //     invalid: {
  //       fontFamily: 'Arial, sans-serif',
  //       color: "#fa755a",
  //       iconColor: "#fa755a"
  //     }
  //   }
  // };

  const isButtonDisabled = processing || disabled || succeeded;
  const hasAddedCard = (cardDataList && cardDataList.length > 0) || succeeded;

  return (
    <Layout
      variant="dashboard"
      header={t("onboardingAddCard.header")}
      subheader={
        <div className="text-neutral-dark text-base font-normal mt-2">
          {t("onboardingAddCard.subheader")}
        </div>
      }
    >
      <div className="w-full max-w-5xl px-0 py-4 space-y-[18px]">
        <div className="space-y-[18px]">
          {!isAddressSubmitted && 
            <div className="mt-2 max-w-2xl rounded-xl bg-attention-lightest px-6 py-3 text-attention-darkest">
              {t("billing.addressAlert", { organizationLabel: t("nav.organization") })}
            </div>
          }
          <div className="flex flex-col gap-[18px] lg:flex-row lg:items-start lg:gap-[18px]">
            <div className="flex-1 min-w-0 space-y-[18px] pr-4">
              <Card
                variant="paymentMethod"
                title={t("billing.cardsTitle")}
                buttonText={t("billing.addCard")}
                icon={<Icon variant="creditCard" size={25}/>}
                iconSubtitle={t("billing.noCards")}
                onClick={isAddressSubmitted ? onPressAddCard :  undefined}
                hasTransactionFee={false}
                hasAddButton={!(Array.isArray(cardDataList) && cardDataList.length > 0)}
                data={cardDataList}
                selectedIndex={selectedCardIndex}
              />

              {hasAddedCard ? (
                <div className="flex flex-col items-start">
                  <div className="text-neutral-dark text-base font-normal">
                    {t("onboardingAddCard.addTeamMembersPrompt")}
                  </div>
                  <SButton
                    onClick={onContinuePressed}
                    variant="default"
                    className="mt-3 w-56 hover:bg-primary-dark hover:opacity-80 disabled disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {t("common.continue")}
                  </SButton>
                </div>
              ) : null}
            </div>
            <div className="w-full max-w-xs">
              <Card
                variant="subscription"
                planType={SubscriptionPlansTypes.basic}
                planAmount={planAmount}
                subscriptionPlanData={subscriptionPlanData[SubscriptionPlansTypes.basic]}
                // todo: add the plan pricing as a variable
                onClick={() => handleUpgradeClick()}
                onSendEmail={() => handleSendEmailClick()}
              />
            </div>
          </div>
        </div>

        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={null}
          isContinueDisabled={true}
        />
        <div>
          {modalIsOpen ? (
            <ReactModal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              style={globalStyles.generalModal}
              shouldCloseOnOverlayClick={true}
              ariaHideApp={false}
            >
              <div className="min-h-[250px]">
                <div className="font-semibold text-2xl flex flex-row justify-center py-2">{t("billing.modalTitle")}</div>
                <form className="p-2" id="payment-form" onSubmit={handleSubmit}>
                  <div className="flex flex-col justify-center items-center">
                    <div className="relative w-full flex justify-center">
                      {!isPaymentElementReady && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <Spinner variant="large" />
                        </div>
                      )}
                      <div className={isPaymentElementReady ? "w-full" : "w-full opacity-0"}>
                        <PaymentElement onReady={() => setPaymentElementReady(true)} />
                      </div>
                    </div>
                    <div className="mt-6 w-56">
                      <Button
                        onClick={isButtonDisabled ? undefined : handleSubmit}
                        variant="primary"
                        label={processing ? t("billing.registering") : t("billing.registerCard")}
                        textWeight={"semibold"}
                        color="primary-medium"
                        stretch
                      />
                    </div>
                    {/* Show any error that happens when processing the card add */}
                    {error && (
                      <div className="card-error" role="alert">
                        {error}
                      </div>
                    )}
                    {/* Show a success message upon completion */}
                    <p className={succeeded ? "result-message" : "result-message hidden"}>
                      {t("billing.cardAdded")}
                    </p>
                  </div>
                </form>
              </div>
            </ReactModal>
          ) : null}        
        </div>
      </div>
    </Layout>
  );
};
